import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandS,
  BrandXS,
  BrandXXS,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { ContenderBonus } from '#src/components/lineup/Contender/ContenderFooter/ContenderBonus/index.tsx';

import type { RankRewardsRanking_so5Ranking } from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
  display: flex;
  gap: var(--triple-unit);
  justify-content: space-between;
  align-items: start;
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
  padding: var(--padding, var(--intermediate-unit));

  &.isMyRanking {
    --border-gradient: linear-gradient(
      90deg,
      #294bff 0%,
      rgba(41, 75, 255, 0.3) 100%
    );
    background: linear-gradient(
        90deg,
        rgba(41, 75, 255, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      rgba(255, 255, 255, 0.11);
  }

  &.rank-1 {
    --border-gradient: var(--c-gradient-gold);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(212, 150, 31, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }
  &.rank-2 {
    --border-gradient: var(--c-gradient-silver);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(214, 228, 233, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }
  &.rank-3 {
    --border-gradient: var(--c-gradient-bronze);
    background: linear-gradient(
        0deg,
        rgba(255, 255, 255, 0.11),
        rgba(255, 255, 255, 0.11)
      ),
      linear-gradient(90deg, rgba(246, 158, 93, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
  }

  &.isSelected {
    --border-gradient: linear-gradient(
      90deg,
      #294bff 0%,
      rgba(41, 75, 255, 0.3) 100%
    );
    background: linear-gradient(
        90deg,
        rgba(41, 75, 255, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      ),
      rgba(255, 255, 255, 0.11);
  }

  &.rank-1,
  &.rank-2,
  &.rank-3,
  &.isMyRanking,
  &.isSelected {
    ${borderGradientStyle}
  }
`;

const Header = styled(SBHorizontal)`
  align-items: flex-end;
`;

const Score = styled(BrandS)`
  display: inline-flex;
  gap: 2px;
`;

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  text-align: left;
`;

type Props = {
  so5Ranking: RankRewardsRanking_so5Ranking;
  isMyRanking?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  isSelected?: boolean;
};

export const Ranking = ({
  so5Ranking,
  isMyRanking,
  isSelected,
  children,
  onClick,
}: Props) => {
  return (
    <Root
      key={so5Ranking.id}
      as={onClick ? StyledButtonBase : undefined}
      onClick={onClick}
      className={classNames(
        so5Ranking.eligibleDivisionChange,
        `rank-${so5Ranking.ranking}`,
        {
          isMyRanking,
          isSelected,
        }
      )}
    >
      <Vertical gap={1.5}>
        <Header gap={1}>
          <Vertical gap={0.5}>
            <LabelM bold>
              <FormattedMessage
                {...fantasy.gw}
                values={{
                  week: so5Ranking.so5Lineup.so5Fixture.seasonGameWeek,
                }}
              />
            </LabelM>
            <BrandS>
              <FormattedMessage
                {...glossary.ordinal}
                values={{
                  ordinal: so5Ranking.so5Lineup.cancelledAt
                    ? '-'
                    : so5Ranking.ranking ?? 0,
                }}
              />
            </BrandS>
          </Vertical>
          <Horizontal>
            {so5Ranking.so5Lineup.cancelledAt ? (
              <BrandXS color="var(--c-yellow-400)">
                <FormattedMessage
                  id="RankRewards.Ranking.canceled"
                  defaultMessage="Canceled lineup"
                />
              </BrandXS>
            ) : (
              <>
                <Score>
                  <FormattedMessage
                    {...fantasy.ordinalPoints}
                    values={{
                      score: so5Ranking.score,
                      sup: (...chunks: ReactNode[]) => (
                        <BrandXXS as="sup" color="var(--c-nd-600)" bold>
                          {chunks}
                        </BrandXXS>
                      ),
                    }}
                  />
                </Score>
                {!!so5Ranking.so5Lineup.bonusPoints && (
                  <ContenderBonus
                    bonusRating={so5Ranking.so5Lineup.bonusRating}
                    bonus={so5Ranking.so5Lineup.bonusPoints}
                  />
                )}
              </>
            )}
          </Horizontal>
        </Header>
        {children}
      </Vertical>
    </Root>
  );
};

Ranking.fragments = {
  so5Ranking: gql`
    fragment RankRewardsRanking_so5Ranking on So5Ranking {
      id
      eligibleDivisionChange
      ranking
      score
      so5Lineup {
        id
        name
        bonusPoints
        bonusRating
        cancelledAt
        managerTeam {
          id
          name
        }
        so5Fixture {
          slug
          seasonGameWeek
        }
      }
    }
  ` as TypedDocumentNode<RankRewardsRanking_so5Ranking>,
};
